import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Settings, Gift, Search, Megaphone, CheckCircle, XCircle, Plus, LogOut, BookOpen, Edit, Image, Trash2, Eye, Info, AlertTriangle, Sparkles, Clock, Euro, Users, Heart, ShoppingBag, Printer, Phone, Mail, User, Send, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { loginAdmin, logoutAdmin, checkAdminSession, setUnauthorizedHandler, getAdminToken } from '../lib/adminAuth';
import { escapeHtml } from '../lib/htmlEscape';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_hilanta-wellness/artifacts/yzd7rzdb_76b87d66-fa32-4fab-93bc-4ef8dc416573.jpg";

// Couleurs par catégorie pour les services
const getCategoryColor = (category) => {
  const colors = {
    massage: { bg: 'bg-orange-50', border: 'border-orange-200', borderSelected: 'border-orange-500', title: 'text-orange-700', price: 'text-orange-600' },
    feminin: { bg: 'bg-[#E8F0E9]', border: 'border-[#A8C5AE]', borderSelected: 'border-[#4A6D5C]', title: 'text-[#4A6D5C]', price: 'text-[#4A6D5C]' },
    naturopathie: { bg: 'bg-amber-50', border: 'border-amber-200', borderSelected: 'border-amber-500', title: 'text-amber-700', price: 'text-amber-600' },
  };
  return colors[category] || colors.massage;
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('giftcards');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Gift cards state
  const [giftCards, setGiftCards] = useState([]);
  const [giftCardStats, setGiftCardStats] = useState({ total: 0, used: 0, available: 0, expired: 0, total_amount: 0 });
  const [giftCardFilter, setGiftCardFilter] = useState('');
  const [giftCardSearch, setGiftCardSearch] = useState('');
  const [loadingGiftCards, setLoadingGiftCards] = useState(false);
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    announcement_type: 'info',
    background_color: '',
    text_color: '',
    is_active: true
  });
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [loadingBlog, setLoadingBlog] = useState(false);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: '',
    is_published: true
  });
  const [savingPost, setSavingPost] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Create gift card on-site state
  const [showCreateGiftCard, setShowCreateGiftCard] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [duoServices, setDuoServices] = useState({});
  const [giftCardForm, setGiftCardForm] = useState({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    recipient_name: '',
    personal_message: '',
    payment_method: 'cash' // cash, card, transfer
  });
  const [creatingGiftCard, setCreatingGiftCard] = useState(false);
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);

  // Phone lookup for admin
  const lookupByPhone = async (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 5) { setPhoneSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/clients/lookup?phone=${digits}`);
      setPhoneSuggestions(res.data || []);
      setShowPhoneSuggestions(res.data && res.data.length > 0);
    } catch { setPhoneSuggestions([]); }
  };

  const applyClientSuggestion = (client) => {
    setGiftCardForm(prev => ({
      ...prev,
      buyer_name: client.name || prev.buyer_name,
      buyer_email: client.email || prev.buyer_email,
      buyer_phone: client.phone || prev.buyer_phone,
    }));
    setShowPhoneSuggestions(false);
    setPhoneSuggestions([]);
  };

  // Mot de passe admin : géré côté serveur (bcrypt + JWT 8h via /api/auth/admin-login)
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await loginAdmin(password, 'admin');
      setIsAuthenticated(true);
      toast.success('Bienvenue dans l\'espace admin !');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error('Trop de tentatives. Réessayez dans 5 minutes.');
      } else if (status === 401) {
        toast.error('Mot de passe incorrect');
      } else {
        toast.error('Erreur de connexion. Vérifiez votre réseau.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutAdmin();
    setIsAuthenticated(false);
    setPassword('');
    toast.info('Déconnexion réussie');
  };

  // Sync the Planity catalogue into the current DB (used right after a fresh
  // production deployment, when the prod DB doesn't have the services yet).
  const [syncingCatalogue, setSyncingCatalogue] = useState(false);
  const handleSyncCatalogue = async () => {
    if (!window.confirm(
      'Synchroniser le catalogue de prestations Planity ?\n\n' +
      'Cette action met à jour les prestations en base à partir du catalogue ' +
      'officiel (script idempotent). Utile après un nouveau déploiement.'
    )) return;
    setSyncingCatalogue(true);
    try {
      const res = await axios.post(`${API}/admin/sync-services`);
      const { inserted, updated, deactivated, active_total } = res.data;
      toast.success(
        `✓ Catalogue synchronisé : ${inserted} ajoutées, ${updated} mises à jour, ` +
        `${deactivated} désactivées · ${active_total} prestations actives.`,
        { duration: 8000 }
      );
      // Refresh services list in any open create form
      try {
        const r = await axios.get(`${API}/services`);
        setServices(r.data);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncingCatalogue(false);
    }
  };

  // Live sync from https://www.planity.com/hilanta-13004-marseille
  // Pulls the current prices/durations directly from Planity's public page.
  const [syncingPlanity, setSyncingPlanity] = useState(false);
  const handlePlanityLiveSync = async () => {
    if (!window.confirm(
      'Synchroniser en direct depuis Planity ?\n\n' +
      'Cette action récupère les prix et durées actuels depuis\n' +
      'https://www.planity.com/hilanta-13004-marseille\n' +
      'et met à jour votre catalogue.\n\n' +
      'Les prestations existantes sont mises à jour (prix + durée), même si le nom Planity ' +
      'diffère légèrement (appariement automatique).\n' +
      'Les prestations Planity non reconnues sont SIGNALÉES (plus ajoutées automatiquement pour éviter les doublons).\n' +
      '(Les variantes DUO et les abonnements sont ignorés.)'
    )) return;
    setSyncingPlanity(true);
    try {
      const res = await axios.post(`${API}/admin/planity-sync`, {});
      const { updated = [], unmatched = [], removed_duplicates = [], skipped_duplicate = [], skipped_duo = [], unchanged_count = 0, planity_service_count } = res.data;
      const lines = [];
      lines.push(`✓ ${planity_service_count} prestations lues depuis Planity`);
      lines.push(`↻ ${updated.length} prix/durées mis à jour`);
      lines.push(`= ${unchanged_count} inchangées`);
      lines.push(`🧹 ${removed_duplicates.length} doublons supprimés du catalogue`);
      lines.push(`⚠ ${unmatched.length} non reconnues (à vérifier)`);
      lines.push(`⊘ ${skipped_duo.length} DUO + ${skipped_duplicate.length} doublons Planity ignorés`);
      toast.success(lines.join(' · '), { duration: 12000 });
      if (updated.length > 0) {
        // Show the first few price changes in a secondary toast so the owner sees the diff.
        const diff = updated.slice(0, 4).map(u =>
          `• ${u.name}: ${u.old_price}€ → ${u.new_price}€`
        ).join('\n');
        toast.info(diff + (updated.length > 4 ? `\n… et ${updated.length - 4} autres` : ''), { duration: 15000 });
      }
      if (unmatched.length > 0) {
        const list = unmatched.slice(0, 6).map(u => `• ${u.name} (${u.price}€)`).join('\n');
        toast.warning('Prestations Planity non reconnues (ajoutez-les manuellement si besoin) :\n' + list +
          (unmatched.length > 6 ? `\n… et ${unmatched.length - 6} autres` : ''), { duration: 18000 });
      }
      try {
        const r = await axios.get(`${API}/services`);
        setServices(r.data);
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de synchronisation Planity');
    } finally {
      setSyncingPlanity(false);
    }
  };

  // Auto-logout when the server returns 401 (expired session)
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setIsAuthenticated(false);
      setPassword('');
      toast.error('Session expirée, veuillez vous reconnecter');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getAdminToken()) {
        const ok = await checkAdminSession();
        if (!cancelled && ok) setIsAuthenticated(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch gift cards
  useEffect(() => {
    if (isAuthenticated && activeTab === 'giftcards') {
      fetchGiftCards();
      fetchGiftCardStats();
    }
  }, [isAuthenticated, activeTab, giftCardFilter, giftCardSearch]);

  // Fetch blog posts
  useEffect(() => {
    if (isAuthenticated && activeTab === 'blog') {
      fetchBlogPosts();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch announcements
  useEffect(() => {
    if (isAuthenticated && activeTab === 'announcements') {
      fetchAnnouncements();
    }
  }, [isAuthenticated, activeTab]);

  // Email status dashboard
  const [emailStatus, setEmailStatus] = useState({ items: [], summary: { total: 0, sent_today: 0, failed_today: 0, pending_today: 0 } });
  const [loadingEmailStatus, setLoadingEmailStatus] = useState(false);
  const [resendingFor, setResendingFor] = useState('');

  const fetchEmailStatus = async () => {
    setLoadingEmailStatus(true);
    // Auto-retry once on transient network errors (mobile 4G/5G can drop a single request).
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.get(`${API}/admin/email-status?days=14&limit=200`);
        setEmailStatus(res.data);
        setLoadingEmailStatus(false);
        return;
      } catch (e) {
        if (e?.response?.status === 401) break;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
      }
    }
    // Silent failure — the user can hit the "Rafraîchir" button to retry.
    // The previous toast.error here was triggering on every transient network
    // glitch / tab-switch race condition and felt like a hard error to the user.
    setLoadingEmailStatus(false);
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'emails') {
      fetchEmailStatus();
    }
  }, [isAuthenticated, activeTab]);

  const handleResendEmail = async (code) => {
    if (!window.confirm(`Renvoyer la carte cadeau ${code} par email ?`)) return;
    setResendingFor(code);
    try {
      const res = await axios.post(`${API}/admin/gift-cards/${code}/resend-email`);
      if (res.data.ok) {
        toast.success(`Email renvoyé à ${res.data.to}`);
        fetchEmailStatus();
      } else {
        toast.error("L'envoi a échoué — vérifiez les logs serveur");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du renvoi");
    } finally {
      setResendingFor('');
    }
  };

  // ── Promo Codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    valid_until: '',
    max_uses: '',
    is_public: false,
  });

  const fetchPromoCodes = async () => {
    setLoadingPromos(true);
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const res = await axios.get(`${API}/admin/promo-codes`);
        setPromoCodes(res.data);
        setLoadingPromos(false);
        return;
      } catch (e) {
        if (e?.response?.status === 401) break;
        if (attempt === 0) await new Promise((r) => setTimeout(r, 800));
      }
    }
    // Silent failure (see fetchEmailStatus comment).
    setLoadingPromos(false);
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'promo-codes') {
      fetchPromoCodes();
    }
  }, [isAuthenticated, activeTab]);

  const handleCreatePromo = async (e) => {
    e.preventDefault();
    if (!newPromo.code.trim() || !newPromo.discount_value) {
      toast.error('Code et valeur requis');
      return;
    }
    try {
      const payload = {
        code: newPromo.code.trim().toUpperCase(),
        description: newPromo.description.trim(),
        discount_type: newPromo.discount_type,
        discount_value: parseFloat(newPromo.discount_value),
        is_public: !!newPromo.is_public,
      };
      if (newPromo.valid_until) {
        payload.valid_until = new Date(newPromo.valid_until + 'T23:59:59').toISOString();
      }
      if (newPromo.max_uses) {
        payload.max_uses = parseInt(newPromo.max_uses, 10);
      }
      await axios.post(`${API}/admin/promo-codes`, payload);
      toast.success(`Code ${payload.code} créé`);
      setNewPromo({ code: '', description: '', discount_type: 'percentage', discount_value: '', valid_until: '', max_uses: '', is_public: false });
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleTogglePromo = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active });
      toast.success(`Code ${promo.is_active ? 'désactivé' : 'activé'}`);
      fetchPromoCodes();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleBanner = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promo-codes/${promo.id}`, { is_public: !promo.is_public });
      toast.success(promo.is_public ? 'Code retiré du bandeau public' : 'Code affiché en bandeau public');
      fetchPromoCodes();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeletePromo = async (promo) => {
    if (!window.confirm(`Supprimer définitivement le code ${promo.code} ?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${promo.id}`);
      toast.success('Code supprimé');
      fetchPromoCodes();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Fetch services for gift card creation
  useEffect(() => {
    if (isAuthenticated && showCreateGiftCard && services.length === 0) {
      fetchServices();
    }
  }, [isAuthenticated, showCreateGiftCard]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Gift card creation functions
  const canHaveDuo = (service) => {
    return service.category === 'massage' || service.name.toLowerCase().includes('femme enceinte');
  };

  const toggleService = (service) => {
    setSelectedServices(prev => {
      const isSelected = prev.find(s => s.id === service.id);
      if (isSelected) {
        setDuoServices(d => {
          const newDuo = { ...d };
          delete newDuo[service.id];
          return newDuo;
        });
        return prev.filter(s => s.id !== service.id);
      } else {
        return [...prev, service];
      }
    });
  };

  const toggleDuo = (serviceId, e) => {
    e.stopPropagation();
    setDuoServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  const isServiceSelected = (serviceId) => {
    return selectedServices.some(s => s.id === serviceId);
  };

  const getTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      const isDuo = duoServices[service.id] || false;
      return total + (isDuo ? service.price * 2 : service.price);
    }, 0);
  };

  const createGiftCardOnSite = async (e) => {
    e.preventDefault();
    
    if (selectedServices.length === 0) {
      toast.error('Sélectionnez au moins un soin');
      return;
    }
    
    if (!giftCardForm.buyer_name) {
      toast.error('Entrez le nom du client');
      return;
    }

    setCreatingGiftCard(true);
    try {
      const totalPrice = getTotalPrice();
      
      const servicesForCard = selectedServices.map(service => {
        const isDuo = duoServices[service.id] || false;
        return {
          id: service.id,
          name: isDuo ? `${service.name} (DUO)` : service.name,
          duration: service.duration,
          price: isDuo ? service.price * 2 : service.price,
          is_duo: isDuo
        };
      });
      
      const serviceNames = servicesForCard.map(s => s.name).join(' + ');
      
      // Create gift card directly (already paid on-site)
      const response = await axios.post(`${API}/gift-cards/create-onsite`, {
        amount: totalPrice,
        services: servicesForCard,
        service_name: serviceNames,
        buyer_name: giftCardForm.buyer_name,
        buyer_email: giftCardForm.buyer_email || '',
        buyer_phone: giftCardForm.buyer_phone || '',
        recipient_name: giftCardForm.recipient_name || '',
        personal_message: giftCardForm.personal_message,
        payment_method: giftCardForm.payment_method,
        message: giftCardForm.personal_message || `Carte cadeau : ${serviceNames}`
      });
      
      toast.success(`Carte cadeau créée ! Code : ${response.data.code}`);
      
      // Reset form
      setSelectedServices([]);
      setDuoServices({});
      setGiftCardForm({
        buyer_name: '',
        buyer_email: '',
        buyer_phone: '',
        recipient_name: '',
        personal_message: '',
        payment_method: 'cash'
      });
      setShowCreateGiftCard(false);
      
      // Refresh gift cards list
      fetchGiftCards();
      fetchGiftCardStats();
      
    } catch (error) {
      console.error('Error creating gift card:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setCreatingGiftCard(false);
    }
  };

  // Gift cards functions
  const fetchGiftCards = async () => {
    setLoadingGiftCards(true);
    try {
      let url = `${API}/gift-cards`;
      const params = new URLSearchParams();
      if (giftCardFilter) params.append('status', giftCardFilter);
      if (giftCardSearch) params.append('search', giftCardSearch);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setGiftCards(response.data);
    } catch (error) {
      console.error('Error fetching gift cards:', error);
    } finally {
      setLoadingGiftCards(false);
    }
  };

  const fetchGiftCardStats = async () => {
    try {
      const response = await axios.get(`${API}/gift-cards/stats`);
      setGiftCardStats(response.data);
    } catch (error) {
      console.error('Error fetching gift card stats:', error);
    }
  };

  const markGiftCardAsUsed = async (code) => {
    try {
      await axios.post(`${API}/gift-cards/${code}/use`);
      toast.success('Carte cadeau marquée comme utilisée');
      fetchGiftCards();
      fetchGiftCardStats();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteGiftCard = async (code) => {
    if (!window.confirm(`Supprimer la carte cadeau ${code} ?`)) return;
    try {
      await axios.delete(`${API}/gift-cards/${code}`);
      toast.success('Carte cadeau supprimée');
      fetchGiftCards();
      fetchGiftCardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const printGiftCard = (gc) => {
    const expiresDate = gc.expires_at ? new Date(gc.expires_at).toLocaleDateString('fr-FR') : 'N/A';
    // Escape all customer-supplied values before injection — see lib/htmlEscape.js
    const safeService = escapeHtml(gc.service_name || 'Soin bien-être');
    const safeCode = escapeHtml(gc.code);
    const safeExpires = escapeHtml(expiresDate);
    const safeBuyer = escapeHtml(gc.buyer_name || '');
    const safeRecipient = escapeHtml(gc.recipient_name || '');
    const leftSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='240' viewBox='0 0 60 240'><path d='M5 235 Q18 195 32 150 Q46 105 55 60' stroke='#A8C5AE' stroke-width='1.2' fill='none'/><ellipse cx='12' cy='215' rx='12' ry='4' fill='#BFD4C4' transform='rotate(-50 12 215)' opacity='.7'/><ellipse cx='20' cy='192' rx='11' ry='4' fill='#A8C5AE' transform='rotate(-45 20 192)' opacity='.7'/><ellipse cx='28' cy='168' rx='12' ry='4' fill='#C0D5BF' transform='rotate(-42 28 168)' opacity='.7'/><ellipse cx='38' cy='138' rx='11' ry='3.5' fill='#A8C5AE' transform='rotate(-45 38 138)' opacity='.7'/><ellipse cx='46' cy='105' rx='10' ry='3.5' fill='#BFD4C4' transform='rotate(-48 46 105)' opacity='.65'/><circle cx='8' cy='224' r='2' fill='#D4B5B0' opacity='.5'/><circle cx='16' cy='202' r='1.8' fill='#D4B5B0' opacity='.45'/><circle cx='25' cy='178' r='1.5' fill='#D4B5B0' opacity='.4'/></svg>`;
    const rightSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='240' viewBox='0 0 60 240'><path d='M55 5 Q42 45 28 90 Q14 135 5 180' stroke='#A8C5AE' stroke-width='1.2' fill='none'/><ellipse cx='48' cy='25' rx='12' ry='4' fill='#BFD4C4' transform='rotate(50 48 25)' opacity='.7'/><ellipse cx='40' cy='48' rx='11' ry='4' fill='#A8C5AE' transform='rotate(45 40 48)' opacity='.7'/><ellipse cx='32' cy='72' rx='12' ry='4' fill='#C0D5BF' transform='rotate(42 32 72)' opacity='.7'/><ellipse cx='22' cy='102' rx='11' ry='3.5' fill='#A8C5AE' transform='rotate(45 22 102)' opacity='.7'/><ellipse cx='14' cy='135' rx='10' ry='3.5' fill='#BFD4C4' transform='rotate(48 14 135)' opacity='.65'/><circle cx='52' cy='14' r='2' fill='#D4B5B0' opacity='.5'/><circle cx='44' cy='37' r='1.8' fill='#D4B5B0' opacity='.45'/><circle cx='35' cy='60' r='1.5' fill='#D4B5B0' opacity='.4'/></svg>`;
    const logoUrl = `${BACKEND_URL}/api/email-assets/logo.png`;
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<style>
@page { size: 20cm 9.5cm landscape; margin: 0; }
*{box-sizing:border-box;margin:0;padding:0;}
body{width:20cm;height:9.5cm;background:#fff;font-family:Arial,Helvetica,sans-serif;overflow:hidden;}
.card{width:100%;height:100%;display:flex;align-items:stretch;background:#fff;}
.deco{width:1.6cm;flex-shrink:0;display:flex;align-items:flex-end;}
.deco.right{align-items:flex-start;}
.content{flex:1;padding:0.45cm 0.3cm;display:flex;flex-direction:column;align-items:center;}
.logo-wrap{text-align:center;margin-bottom:0.12cm;}
.logo{width:1.6cm;height:1.6cm;border-radius:50%;object-fit:cover;}
.brand{font-size:7.5pt;letter-spacing:4px;font-weight:bold;color:#1A1A1A;margin-top:0.06cm;}
.title{font-size:13pt;letter-spacing:7px;font-weight:bold;color:#1A1A1A;margin:0.18cm 0 0.22cm;}
.fields{width:100%;font-size:6.8pt;}
.field{font-weight:bold;letter-spacing:1px;margin-bottom:0.13cm;color:#1A1A1A;}
.field span{font-weight:normal;}
.row{display:flex;justify-content:space-between;gap:0.3cm;}
.row .field{flex:1;}
hr{border:none;border-top:0.5pt solid #ccc;margin:0.13cm 0;}
.footer{font-size:6pt;text-align:center;color:#555;line-height:1.5;}
.italic{font-style:italic;}
.bold{font-weight:bold;color:#333;}
</style></head>
<body><div class="card">
<div class="deco"><img src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(leftSvg)}" width="60" height="240"/></div>
<div class="content">
  <div class="logo-wrap"><img src="${logoUrl}" class="logo"/><div class="brand">HILANTA</div></div>
  <div class="title">CARTE CADEAU</div>
  <div class="fields">
    <div class="field">PRESTATION : <span>${safeService}</span></div>
    <div class="field">NUMÉRO DE CARTE : <span style="font-family:monospace;letter-spacing:2px;">${safeCode}</span></div>
    <div class="field">VALABLE JUSQU'AU : <span>${safeExpires}</span></div>
    <div class="row">
      <div class="field">OFFERT PAR : <span>${safeBuyer}</span></div>
      <div class="field">POUR : <span>${safeRecipient}</span></div>
    </div>
  </div>
  <hr/>
  <div class="footer">
    <div class="italic">Réservation par téléphone ou sur hilanta.fr</div>
    <div>58 Avenue Maréchal foch, 13004 Marseille &nbsp;|&nbsp; Contact : 09 60 42 18 22 &nbsp;|&nbsp; la.hilanta@gmail.com</div>
    <div class="bold">Non remboursable, non échangeable</div>
  </div>
</div>
<div class="deco right"><img src="data:image/svg+xml;charset=utf-8,${encodeURIComponent(rightSvg)}" width="60" height="240"/></div>
</div></body></html>`;
    const w = window.open('', '_blank', 'width=900,height=450');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  // Announcements functions
  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const response = await axios.get(`${API}/announcements?active_only=false`);
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoadingAnnouncements(false);
    }
  };

  const createAnnouncement = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/announcements`, newAnnouncement);
      toast.success('Annonce créée');
      setNewAnnouncement({ title: '', message: '', announcement_type: 'info', background_color: '', text_color: '', is_active: true });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const toggleAnnouncement = async (id, isActive) => {
    try {
      await axios.patch(`${API}/announcements/${id}`, { is_active: !isActive });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    try {
      await axios.delete(`${API}/announcements/${id}`);
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Blog functions
  const fetchBlogPosts = async () => {
    setLoadingBlog(true);
    try {
      const response = await axios.get(`${API}/blog/posts?include_drafts=true`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoadingBlog(false);
    }
  };

  const uploadBlogImage = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/admin/blog/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBlogForm(prev => ({ ...prev, image_url: response.data.image_url }));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingImage(false);
    }
  };

  const saveBlogPost = async (e) => {
    e.preventDefault();
    setSavingPost(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/posts/${editingPost.id}`, blogForm);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/blog/posts`, blogForm);
        toast.success('Article créé');
      }
      setShowBlogEditor(false);
      setEditingPost(null);
      setBlogForm({ title: '', content: '', excerpt: '', image_url: '', is_published: true });
      fetchBlogPosts();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingPost(false);
    }
  };

  const editPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      image_url: post.image_url || '',
      is_published: post.is_published
    });
    setShowBlogEditor(true);
  };

  const deletePost = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/blog/posts/${id}`);
      toast.success('Article supprimé');
      fetchBlogPosts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Login page
  if (!isAuthenticated) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Administration - Hilanta</title>
        </Helmet>
      <div className="min-h-screen flex items-center justify-center py-12 px-4" data-testid="admin-login">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8">
            <div className="text-center mb-8">
              <img
                src={LOGO_URL}
                alt="Hilanta"
                className="w-20 h-20 mx-auto mb-4 rounded-full object-cover"
                style={{ border: '3px solid #C4A246' }}
              />
              <h1 className="font-heading text-3xl text-warm-brown tracking-wide">Administration</h1>
              <p className="text-warm-brown-light text-sm mt-1 tracking-widest uppercase text-xs">Hilanta</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-warm-brown">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mot de passe"
                  className="mt-1 rounded-xl"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading || password.length < 4}
                className="w-full bg-terracotta hover:bg-terracotta-600 text-white rounded-xl py-3"
                data-testid="admin-login-btn"
              >
                {loginLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Lock className="mr-2" size={18} />
                )}
                Connexion
              </Button>
            </form>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Administration - Hilanta</title>
      </Helmet>
      
      <div className="min-h-screen py-8 px-4" data-testid="admin-dashboard">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <img
                src={LOGO_URL}
                alt="Hilanta"
                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                style={{ border: '2px solid #C4A246' }}
              />
              <div>
                <h1 className="font-heading text-2xl text-warm-brown tracking-wide">Administration Hilanta</h1>
                <p className="text-warm-brown-light text-xs tracking-widest uppercase">Gestion simplifiée</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <PWAInstallPrompt pageType="admin" />
              <Button
                onClick={handlePlanityLiveSync}
                disabled={syncingPlanity}
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50 rounded-xl"
                title="Récupérer les prix et prestations en direct depuis Planity"
                data-testid="sync-planity-live-btn"
              >
                {syncingPlanity ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <RefreshCw className="mr-2" size={18} />
                )}
                Sync Planity (live)
              </Button>
              <Button
                onClick={handleSyncCatalogue}
                disabled={syncingCatalogue}
                variant="outline"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-xl"
                title="Recharger les prestations Planity en base"
                data-testid="sync-catalogue-btn"
              >
                {syncingCatalogue ? (
                  <Loader2 className="animate-spin mr-2" size={18} />
                ) : (
                  <RefreshCw className="mr-2" size={18} />
                )}
                Synchroniser catalogue
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
              >
                <LogOut className="mr-2" size={18} />
                Déconnexion
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setActiveTab('giftcards')}
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'giftcards'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-warm-brown hover:bg-amber-50'
              }`}
            >
              <Gift className="inline mr-2" size={18} />
              Cartes Cadeaux
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'blog'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-warm-brown hover:bg-purple-50'
              }`}
            >
              <BookOpen className="inline mr-2" size={18} />
              Blog
            </button>
            <button
              onClick={() => setActiveTab('announcements')}
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'announcements'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-warm-brown hover:bg-blue-50'
              }`}
            >
              <Megaphone className="inline mr-2" size={18} />
              Annonces
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              data-testid="admin-tab-emails"
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'emails'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-warm-brown hover:bg-emerald-50'
              }`}
            >
              <Send className="inline mr-2" size={18} />
              État des envois
            </button>
            <button
              onClick={() => setActiveTab('promo-codes')}
              data-testid="admin-tab-promo-codes"
              className={`px-4 sm:px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === 'promo-codes'
                  ? 'bg-rose-600 text-white'
                  : 'bg-white text-warm-brown hover:bg-rose-50'
              }`}
            >
              <Sparkles className="inline mr-2" size={18} />
              Codes Promo
            </button>
          </div>

          {/* Gift Cards Tab */}
          {activeTab === 'giftcards' && (
            <div className="space-y-6">
              {/* Button to create gift card on site */}
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowCreateGiftCard(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
                >
                  <Plus className="mr-2" size={18} />
                  Créer une carte cadeau (sur place)
                </Button>
              </div>

              {/* Create Gift Card Modal */}
              {showCreateGiftCard && (
                <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-amber-200">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-2xl text-warm-brown flex items-center gap-2">
                      <ShoppingBag className="text-amber-600" size={24} />
                      Créer une carte cadeau (client sur place)
                    </h2>
                    <button
                      onClick={() => setShowCreateGiftCard(false)}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <XCircle size={24} className="text-gray-500" />
                    </button>
                  </div>

                  <form onSubmit={createGiftCardOnSite} className="space-y-6">
                    {/* Services Selection */}
                    <div>
                      <Label className="text-warm-brown font-medium mb-3 block">
                        Soins à offrir (sélection multiple possible)
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[300px] overflow-y-auto p-2">
                        {services.filter(s => !s.name.includes('Silhouette')).map((service) => {
                          const colors = getCategoryColor(service.category);
                          const isSelected = isServiceSelected(service.id);
                          const showDuoOption = canHaveDuo(service);
                          const isDuo = duoServices[service.id] || false;
                          const displayPrice = isDuo ? service.price * 2 : service.price;
                          
                          return (
                            <div
                              key={service.id}
                              onClick={() => toggleService(service)}
                              className={`p-3 rounded-xl ${colors.bg} border-2 ${
                                isSelected ? colors.borderSelected : colors.border
                              } cursor-pointer transition-all ${isSelected ? 'shadow-md' : 'hover:shadow-sm'}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'bg-amber-500 border-amber-500' : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && <CheckCircle size={12} className="text-white" />}
                                </div>
                                <div className="flex-1">
                                  <p className={`font-medium text-sm ${colors.title}`}>
                                    {service.name}
                                    {isSelected && isDuo && <span className="ml-1">(DUO)</span>}
                                  </p>
                                  <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <Clock size={10} /> {service.duration} min
                                  </p>
                                </div>
                                <span className={`font-bold ${colors.price}`}>{displayPrice}€</span>
                              </div>
                              
                              {isSelected && showDuoOption && (
                                <button
                                  type="button"
                                  onClick={(e) => toggleDuo(service.id, e)}
                                  className={`mt-2 w-full flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${
                                    isDuo 
                                      ? 'bg-amber-500 text-white' 
                                      : 'bg-amber-100 text-amber-700 border border-amber-300'
                                  }`}
                                >
                                  <Users size={12} />
                                  Option DUO x2
                                  {isDuo && <CheckCircle size={12} />}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {selectedServices.length > 0 && (
                        <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                          <p className="text-sm text-amber-800 mb-2">Résumé :</p>
                          <ul className="text-sm text-amber-700 space-y-1">
                            {selectedServices.map(s => (
                              <li key={s.id}>
                                • {s.name} {duoServices[s.id] ? '(DUO)' : ''} - {duoServices[s.id] ? s.price * 2 : s.price}€
                              </li>
                            ))}
                          </ul>
                          <p className="text-lg font-bold text-amber-800 mt-2 pt-2 border-t border-amber-300">
                            Total : {getTotalPrice()}€
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Client Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-warm-brown">Offert par (nom de l'acheteur) *</Label>
                        <Input
                          value={giftCardForm.buyer_name}
                          onChange={(e) => setGiftCardForm({ ...giftCardForm, buyer_name: e.target.value })}
                          placeholder="Nom de l'acheteur"
                          className="mt-1 rounded-xl"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-warm-brown">Pour (bénéficiaire)</Label>
                        <Input
                          value={giftCardForm.recipient_name}
                          onChange={(e) => setGiftCardForm({ ...giftCardForm, recipient_name: e.target.value })}
                          placeholder="Nom de la personne à qui offrir"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-warm-brown">Email (optionnel)</Label>
                        <Input
                          type="email"
                          value={giftCardForm.buyer_email}
                          onChange={(e) => setGiftCardForm({ ...giftCardForm, buyer_email: e.target.value })}
                          placeholder="email@exemple.com"
                          className="mt-1 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label className="text-warm-brown">Téléphone (optionnel)</Label>
                        <div className="relative">
                          <Input
                            value={giftCardForm.buyer_phone}
                            onChange={(e) => {
                              setGiftCardForm({ ...giftCardForm, buyer_phone: e.target.value });
                              lookupByPhone(e.target.value);
                            }}
                            onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 200)}
                            placeholder="06 12 34 56 78"
                            className="mt-1 rounded-xl"
                          />
                          {showPhoneSuggestions && phoneSuggestions.length > 0 && (
                            <div className="absolute z-20 top-full left-0 right-0 bg-white border border-warm-brown/20 rounded-xl shadow-lg mt-1 overflow-hidden">
                              {phoneSuggestions.map((c, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => applyClientSuggestion(c)}
                                  className="w-full text-left px-4 py-3 hover:bg-sand transition-colors border-b border-gray-100 last:border-0"
                                >
                                  <p className="font-medium text-warm-brown text-sm">{c.name}</p>
                                  <p className="text-xs text-warm-brown-light">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-warm-brown">Mode de paiement</Label>
                        <select
                          value={giftCardForm.payment_method}
                          onChange={(e) => setGiftCardForm({ ...giftCardForm, payment_method: e.target.value })}
                          className="w-full mt-1 px-4 py-2 rounded-xl border border-gray-200"
                        >
                          <option value="cash">Espèces</option>
                          <option value="card">Carte bancaire</option>
                          <option value="transfer">Virement</option>
                        </select>
                      </div>
                    </div>

                    {/* Personal Message */}
                    <div>
                      <Label className="text-warm-brown">Petit mot personnalisé (optionnel)</Label>
                      <textarea
                        value={giftCardForm.personal_message}
                        onChange={(e) => setGiftCardForm({ ...giftCardForm, personal_message: e.target.value })}
                        placeholder="Message à inscrire sur la carte cadeau..."
                        className="w-full mt-1 rounded-xl border border-gray-200 p-3 min-h-[80px]"
                        maxLength={300}
                      />
                    </div>

                    {/* Submit */}
                    <div className="flex gap-3">
                      <Button
                        type="submit"
                        disabled={creatingGiftCard || selectedServices.length === 0}
                        className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-3"
                      >
                        {creatingGiftCard ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        ) : (
                          <Gift className="mr-2" size={18} />
                        )}
                        Créer la carte cadeau {selectedServices.length > 0 && `(${getTotalPrice()}€)`}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowCreateGiftCard(false)}
                        className="rounded-xl"
                      >
                        Annuler
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-md">
                  <p className="text-sm text-warm-brown-light">Total vendues</p>
                  <p className="text-2xl font-bold text-warm-brown">{giftCardStats.total}</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 shadow-md">
                  <p className="text-sm text-green-600">Disponibles</p>
                  <p className="text-2xl font-bold text-green-700">{giftCardStats.available}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 shadow-md">
                  <p className="text-sm text-blue-600">Utilisées</p>
                  <p className="text-2xl font-bold text-blue-700">{giftCardStats.used}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 shadow-md">
                  <p className="text-sm text-amber-600">Total CA</p>
                  <p className="text-2xl font-bold text-amber-700">{giftCardStats.total_amount}€</p>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="font-heading text-xl text-warm-brown mb-4 flex items-center gap-2">
                  <Gift size={20} className="text-amber-600" />
                  Gestion des cartes cadeaux
                </h2>
                
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      value={giftCardSearch}
                      onChange={(e) => setGiftCardSearch(e.target.value)}
                      placeholder="Rechercher par code ou nom..."
                      className="pl-10 rounded-xl"
                    />
                  </div>
                  <select
                    value={giftCardFilter}
                    onChange={(e) => setGiftCardFilter(e.target.value)}
                    className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-warm-brown"
                  >
                    <option value="">Toutes les cartes</option>
                    <option value="available">Disponibles</option>
                    <option value="used">Utilisées</option>
                    <option value="expired">Expirées</option>
                  </select>
                </div>

                {loadingGiftCards ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : giftCards.length === 0 ? (
                  <div className="text-center py-8 text-warm-brown-light">
                    <Gift size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Aucune carte cadeau trouvée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {giftCards.map((gc) => (
                      <div
                        key={gc.id || gc.code}
                        className={`p-4 rounded-xl border-2 ${
                          gc.is_used 
                            ? 'bg-gray-50 border-gray-200' 
                            : gc.is_expired 
                              ? 'bg-red-50 border-red-200'
                              : 'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                          <div className="flex-1">
                            {/* Code + status */}
                            <div className="flex items-center gap-3 mb-3">
                              <span className={`px-3 py-1 rounded-full text-sm font-mono font-bold ${
                                gc.is_used 
                                  ? 'bg-gray-200 text-gray-600' 
                                  : gc.is_expired
                                    ? 'bg-red-200 text-red-700'
                                    : 'bg-amber-200 text-amber-800'
                              }`}>
                                {gc.code}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                gc.is_used 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : gc.is_expired
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-green-100 text-green-700'
                              }`}>
                                {gc.status_display || (gc.is_used ? 'Utilisée' : gc.is_expired ? 'Expirée' : 'Disponible')}
                              </span>
                              {gc.created_onsite && (
                                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">Sur place</span>
                              )}
                            </div>

                            {/* Two column layout: left = soin/montant, right = client */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {/* Left: Soin info */}
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs text-warm-brown-light uppercase tracking-wide mb-1.5 font-medium">Carte cadeau</p>
                                <p className="text-sm text-warm-brown"><strong>Soin :</strong> {gc.service_name || 'Non spécifié'}</p>
                                <p className="text-sm text-warm-brown"><strong>Montant :</strong> <span className="font-bold text-amber-700">{gc.amount}€</span></p>
                                {gc.payment_method && gc.created_onsite && (
                                  <p className="text-sm text-warm-brown"><strong>Paiement :</strong> {gc.payment_method === 'cash' ? 'Espèces' : gc.payment_method === 'card' ? 'Carte' : 'Virement'}</p>
                                )}
                                <p className="text-xs text-warm-brown-light mt-1.5">
                                  Expire le : {gc.expires_at ? new Date(gc.expires_at).toLocaleDateString('fr-FR') : 'Non défini'}
                                </p>
                              </div>

                              {/* Right: Client coordinates */}
                              <div className="bg-white/60 rounded-lg p-3">
                                <p className="text-xs text-warm-brown-light uppercase tracking-wide mb-1.5 font-medium">Coordonnées client</p>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1.5 text-sm text-warm-brown">
                                    <User size={13} className="text-warm-brown-light flex-shrink-0" />
                                    <span><strong>Offert par :</strong> {gc.buyer_name || 'Anonyme'}</span>
                                  </div>
                                  {gc.recipient_name && (
                                    <div className="flex items-center gap-1.5 text-sm text-warm-brown">
                                      <Heart size={13} className="text-pink-400 flex-shrink-0" />
                                      <span><strong>Pour :</strong> {gc.recipient_name}</span>
                                    </div>
                                  )}
                                  {gc.buyer_phone && (
                                    <div className="flex items-center gap-1.5 text-sm text-warm-brown">
                                      <Phone size={13} className="text-green-600 flex-shrink-0" />
                                      <a href={`tel:${gc.buyer_phone}`} className="hover:text-terracotta hover:underline">
                                        {gc.buyer_phone}
                                      </a>
                                    </div>
                                  )}
                                  {gc.buyer_email && (
                                    <div className="flex items-center gap-1.5 text-sm text-warm-brown">
                                      <Mail size={13} className="text-blue-500 flex-shrink-0" />
                                      <a href={`mailto:${gc.buyer_email}`} className="hover:text-terracotta hover:underline truncate">
                                        {gc.buyer_email}
                                      </a>
                                    </div>
                                  )}
                                  {!gc.buyer_phone && !gc.buyer_email && (
                                    <p className="text-xs text-warm-brown-light italic">Pas de coordonnées</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Personal message if any */}
                            {gc.personal_message && (
                              <div className="mt-2 pl-3 border-l-2 border-amber-300 text-xs italic text-warm-brown-light">
                                &ldquo;{gc.personal_message}&rdquo;
                              </div>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex sm:flex-col gap-2 flex-shrink-0">
                            {!gc.is_used && !gc.is_expired && (
                              <button
                                onClick={() => markGiftCardAsUsed(gc.code)}
                                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium flex items-center gap-1.5"
                              >
                                <CheckCircle size={14} />
                                Marquer utilisée
                              </button>
                            )}
                            <button
                              onClick={() => printGiftCard(gc)}
                              className="px-3 py-2 bg-warm-brown/10 hover:bg-warm-brown/20 text-warm-brown rounded-lg text-xs flex items-center gap-1.5"
                              title="Imprimer la carte"
                            >
                              <Printer size={14} />
                              Imprimer
                            </button>
                            {(gc.is_used || gc.is_expired) && (
                              <button
                                onClick={() => deleteGiftCard(gc.code)}
                                className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg text-xs flex items-center gap-1.5"
                                title="Supprimer"
                              >
                                <Trash2 size={14} />
                                Supprimer
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === 'blog' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl shadow-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-2xl text-white">Gestion du Blog</h2>
                    <p className="text-white/80">Créez et gérez vos articles</p>
                  </div>
                  <Button
                    onClick={() => {
                      setEditingPost(null);
                      setBlogForm({ title: '', content: '', excerpt: '', image_url: '', is_published: true });
                      setShowBlogEditor(true);
                    }}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                  >
                    <Plus className="mr-2" size={18} />
                    Nouvel article
                  </Button>
                </div>
              </div>

              {showBlogEditor ? (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="font-heading text-xl text-warm-brown mb-4">
                    {editingPost ? 'Modifier l\'article' : 'Nouvel article'}
                  </h3>
                  <form onSubmit={saveBlogPost} className="space-y-4">
                    <div>
                      <Label className="text-warm-brown">Titre</Label>
                      <Input
                        value={blogForm.title}
                        onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                        className="mt-1 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-warm-brown">Extrait (aperçu)</Label>
                      <Input
                        value={blogForm.excerpt}
                        onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="text-warm-brown">Contenu</Label>
                      <textarea
                        value={blogForm.content}
                        onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })}
                        className="w-full mt-1 rounded-xl border border-gray-200 p-3 min-h-[200px]"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-warm-brown">Image</Label>
                      <div className="flex items-center gap-4 mt-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => e.target.files[0] && uploadBlogImage(e.target.files[0])}
                          className="hidden"
                          id="blog-image"
                        />
                        <label
                          htmlFor="blog-image"
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl cursor-pointer flex items-center gap-2"
                        >
                          <Image size={18} />
                          {uploadingImage ? 'Upload...' : 'Choisir une image'}
                        </label>
                        {blogForm.image_url && (
                          <img src={blogForm.image_url} alt="Preview" className="h-16 w-16 object-cover rounded-lg" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="published"
                        checked={blogForm.is_published}
                        onChange={(e) => setBlogForm({ ...blogForm, is_published: e.target.checked })}
                      />
                      <Label htmlFor="published" className="text-warm-brown">Publié</Label>
                    </div>
                    <div className="flex gap-3">
                      <Button type="submit" disabled={savingPost} className="bg-purple-600 hover:bg-purple-700">
                        {savingPost ? 'Sauvegarde...' : 'Sauvegarder'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowBlogEditor(false)}>
                        Annuler
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="font-heading text-xl text-warm-brown mb-4">Articles ({blogPosts.length})</h3>
                  {loadingBlog ? (
                    <div className="flex justify-center py-8">
                      <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : blogPosts.length === 0 ? (
                    <div className="text-center py-8 text-warm-brown-light">
                      <BookOpen size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Aucun article</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {blogPosts.map((post) => (
                        <div key={post.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-4">
                            {post.image_url && (
                              <img src={post.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                            )}
                            <div>
                              <p className="font-medium text-warm-brown">{post.title}</p>
                              <p className="text-sm text-warm-brown-light">
                                {post.is_published ? '✅ Publié' : '📝 Brouillon'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => editPost(post)} className="p-2 hover:bg-gray-200 rounded-lg">
                              <Edit size={18} className="text-blue-600" />
                            </button>
                            <button onClick={() => deletePost(post.id)} className="p-2 hover:bg-gray-200 rounded-lg">
                              <Trash2 size={18} className="text-red-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="font-heading text-xl text-warm-brown mb-4 flex items-center gap-2">
                  <Plus size={20} className="text-blue-600" />
                  Créer une annonce
                </h2>
                
                <form onSubmit={createAnnouncement} className="space-y-4">
                  <div>
                    <Label className="text-warm-brown">Titre</Label>
                    <Input
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                      className="mt-1 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-warm-brown">Message</Label>
                    <textarea
                      value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                      className="w-full mt-1 rounded-xl border border-gray-200 p-3 min-h-[100px]"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-warm-brown">Type</Label>
                    <select
                      value={newAnnouncement.announcement_type}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, announcement_type: e.target.value })}
                      className="w-full mt-1 px-4 py-2 rounded-xl border border-gray-200"
                    >
                      <option value="info">Info (bleu par défaut)</option>
                      <option value="promotion">Promotion (orange par défaut)</option>
                      <option value="warning">Avertissement (rouge par défaut)</option>
                      <option value="new_service">Nouveau service (violet par défaut)</option>
                    </select>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-blue-900 font-medium">
                      Personnaliser les couleurs (optionnel)
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-warm-brown text-sm">Couleur de fond</Label>
                        <div className="flex gap-2 mt-1 items-center">
                          <input
                            type="color"
                            value={newAnnouncement.background_color || '#3B82F6'}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, background_color: e.target.value })}
                            className="h-10 w-12 rounded-lg cursor-pointer border border-gray-200"
                            data-testid="announcement-bg-picker"
                          />
                          <input
                            type="text"
                            value={newAnnouncement.background_color}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, background_color: e.target.value })}
                            placeholder="laisser vide = défaut"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 font-mono text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-warm-brown text-sm">Couleur du texte</Label>
                        <div className="flex gap-2 mt-1 items-center">
                          <input
                            type="color"
                            value={newAnnouncement.text_color || '#FFFFFF'}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, text_color: e.target.value })}
                            className="h-10 w-12 rounded-lg cursor-pointer border border-gray-200"
                            data-testid="announcement-text-picker"
                          />
                          <input
                            type="text"
                            value={newAnnouncement.text_color}
                            onChange={(e) => setNewAnnouncement({ ...newAnnouncement, text_color: e.target.value })}
                            placeholder="laisser vide = blanc"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 font-mono text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    {(newAnnouncement.background_color || newAnnouncement.text_color || newAnnouncement.title) && (
                      <div className="rounded-lg px-4 py-3 mt-2 text-center font-medium"
                        style={{
                          background: newAnnouncement.background_color || '#3B82F6',
                          color: newAnnouncement.text_color || '#FFFFFF',
                        }}
                      >
                        {newAnnouncement.title || 'Aperçu'} {newAnnouncement.message && `— ${newAnnouncement.message}`}
                      </div>
                    )}
                  </div>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="mr-2" size={18} />
                    Créer l'annonce
                  </Button>
                </form>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="font-heading text-xl text-warm-brown mb-4">Toutes les annonces</h2>
                {loadingAnnouncements ? (
                  <div className="flex justify-center py-8">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : announcements.length === 0 ? (
                  <div className="text-center py-8 text-warm-brown-light">
                    <Megaphone size={48} className="mx-auto mb-4 opacity-30" />
                    <p>Aucune annonce</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {announcements.map((ann) => (
                      <div key={ann.id} className={`p-4 rounded-xl border-2 ${
                        ann.is_active ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-warm-brown">{ann.title}</p>
                            <p className="text-sm text-warm-brown-light">{ann.message}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              ann.announcement_type === 'promo' ? 'bg-amber-100 text-amber-700' :
                              ann.announcement_type === 'warning' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {ann.announcement_type}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => toggleAnnouncement(ann.id, ann.is_active)}
                              className={`px-3 py-1 rounded-lg text-sm ${
                                ann.is_active ? 'bg-gray-200 text-gray-600' : 'bg-green-600 text-white'
                              }`}
                            >
                              {ann.is_active ? 'Désactiver' : 'Activer'}
                            </button>
                            <button
                              onClick={() => deleteAnnouncement(ann.id)}
                              className="p-2 hover:bg-red-100 rounded-lg"
                            >
                              <Trash2 size={18} className="text-red-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* État des envois Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-6" data-testid="admin-emails-panel">
              {/* Stats summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl shadow p-5">
                  <p className="text-xs uppercase tracking-wider text-warm-brown-light mb-1">Cartes (14j)</p>
                  <p className="text-3xl font-bold text-warm-brown" data-testid="email-stat-total">{emailStatus.summary.total}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl shadow p-5">
                  <p className="text-xs uppercase tracking-wider text-emerald-700 mb-1">Envoyés aujourd'hui</p>
                  <p className="text-3xl font-bold text-emerald-700" data-testid="email-stat-sent">{emailStatus.summary.sent_today}</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-2xl shadow p-5">
                  <p className="text-xs uppercase tracking-wider text-red-700 mb-1">Échecs aujourd'hui</p>
                  <p className="text-3xl font-bold text-red-700" data-testid="email-stat-failed">{emailStatus.summary.failed_today}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-2xl shadow p-5">
                  <p className="text-xs uppercase tracking-wider text-amber-700 mb-1">En attente paiement</p>
                  <p className="text-3xl font-bold text-amber-700" data-testid="email-stat-pending">{emailStatus.summary.pending_today}</p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-warm-brown-light">
                  Suivi des cartes cadeaux et de leur email de livraison (14 derniers jours).
                </p>
                <Button
                  onClick={fetchEmailStatus}
                  disabled={loadingEmailStatus}
                  variant="outline"
                  className="rounded-xl"
                  data-testid="email-refresh-btn"
                >
                  <RefreshCw className={`mr-2 ${loadingEmailStatus ? 'animate-spin' : ''}`} size={16} />
                  Rafraîchir
                </Button>
              </div>

              {/* Table */}
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                {loadingEmailStatus ? (
                  <div className="p-12 text-center text-warm-brown-light">Chargement…</div>
                ) : emailStatus.items.length === 0 ? (
                  <div className="p-12 text-center text-warm-brown-light">
                    <Send className="mx-auto mb-3 opacity-30" size={32} />
                    Aucune carte cadeau sur les 14 derniers jours.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-warm-brown/5 text-warm-brown text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">Code</th>
                          <th className="px-4 py-3 text-left">Acheteur</th>
                          <th className="px-4 py-3 text-left">Bénéficiaire</th>
                          <th className="px-4 py-3 text-right">Montant</th>
                          <th className="px-4 py-3 text-center">Paiement</th>
                          <th className="px-4 py-3 text-center">Email</th>
                          <th className="px-4 py-3 text-left">Dernier envoi</th>
                          <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-warm-brown/10">
                        {emailStatus.items.map((it) => {
                          const isPaid = it.payment_status === 'paid';
                          const sentBadge = it.email_status === 'sent'
                            ? { color: 'bg-emerald-100 text-emerald-700', label: 'Envoyé', Icon: CheckCircle }
                            : it.email_status === 'failed'
                              ? { color: 'bg-red-100 text-red-700', label: 'Échec', Icon: AlertCircle }
                              : { color: 'bg-amber-100 text-amber-700', label: 'En attente', Icon: Clock };
                          const SentIcon = sentBadge.Icon;
                          return (
                            <tr key={it.code} className="hover:bg-warm-brown/[.02]" data-testid={`email-row-${it.code}`}>
                              <td className="px-4 py-3 font-mono text-xs text-warm-brown">{it.code}</td>
                              <td className="px-4 py-3">
                                <div className="font-medium text-warm-brown">{it.buyer_name || '—'}</div>
                                <div className="text-xs text-warm-brown-light">{it.buyer_email || ''}</div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-warm-brown">{it.recipient_name || '—'}</div>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-warm-brown">
                                {it.amount ? `${it.amount} €` : '—'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {isPaid ? 'Payé' : (it.payment_status || 'En attente')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sentBadge.color}`}>
                                  <SentIcon size={12} />
                                  {sentBadge.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-warm-brown-light">
                                {it.last_email_sent_at ? (
                                  <div>
                                    <div>{new Date(it.last_email_sent_at).toLocaleString('fr-FR')}</div>
                                    {it.last_email_to && <div className="font-mono">→ {it.last_email_to}</div>}
                                    {it.last_email_pdf_kb != null && <div>PDF {it.last_email_pdf_kb} KB</div>}
                                  </div>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {isPaid && (
                                  <Button
                                    onClick={() => handleResendEmail(it.code)}
                                    disabled={resendingFor === it.code}
                                    size="sm"
                                    variant="outline"
                                    className="text-xs rounded-lg"
                                    data-testid={`email-resend-${it.code}`}
                                  >
                                    {resendingFor === it.code ? (
                                      <RefreshCw className="animate-spin" size={14} />
                                    ) : (
                                      <><Send size={14} className="mr-1" />Renvoyer</>
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Promo Codes Tab */}
          {activeTab === 'promo-codes' && (
            <div className="space-y-6" data-testid="admin-promo-codes-panel">
              {/* Create form */}
              <div className="bg-white rounded-2xl shadow p-6">
                <h3 className="text-xl font-semibold text-warm-brown mb-4 flex items-center gap-2">
                  <Sparkles size={20} className="text-rose-600" />
                  Créer un nouveau code promo
                </h3>
                <form onSubmit={handleCreatePromo} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">Code <span className="text-rose-500">*</span></label>
                    <input
                      type="text"
                      value={newPromo.code}
                      onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                      placeholder="NOEL2026"
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 font-mono"
                      data-testid="promo-input-code"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">Description (optionnelle)</label>
                    <input
                      type="text"
                      value={newPromo.description}
                      onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
                      placeholder="Promotion fêtes de fin d'année"
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid="promo-input-description"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">Type</label>
                    <select
                      value={newPromo.discount_type}
                      onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid="promo-input-type"
                    >
                      <option value="percentage">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (€)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">
                      Valeur {newPromo.discount_type === 'percentage' ? '(%)' : '(€)'} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={newPromo.discount_type === 'percentage' ? '90' : undefined}
                      value={newPromo.discount_value}
                      onChange={(e) => setNewPromo({ ...newPromo, discount_value: e.target.value })}
                      placeholder={newPromo.discount_type === 'percentage' ? '20' : '10'}
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid="promo-input-value"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">Expiration (optionnel)</label>
                    <input
                      type="date"
                      value={newPromo.valid_until}
                      onChange={(e) => setNewPromo({ ...newPromo, valid_until: e.target.value })}
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid="promo-input-valid-until"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-warm-brown-light mb-1 block">Utilisations max (optionnel)</label>
                    <input
                      type="number"
                      min="1"
                      value={newPromo.max_uses}
                      onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                      placeholder="Illimité si vide"
                      className="w-full px-3 py-2 border border-warm-brown/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300"
                      data-testid="promo-input-max-uses"
                    />
                  </div>
                  <div className="md:col-span-3 flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
                    <input
                      type="checkbox"
                      id="promo-input-is-public"
                      checked={!!newPromo.is_public}
                      onChange={(e) => setNewPromo({ ...newPromo, is_public: e.target.checked })}
                      className="w-5 h-5 accent-rose-600 cursor-pointer"
                      data-testid="promo-input-is-public"
                    />
                    <label htmlFor="promo-input-is-public" className="text-sm text-warm-brown cursor-pointer select-none">
                      <strong className="text-rose-700">Afficher en bandeau public</strong> en haut du site
                      <span className="block text-xs text-warm-brown-light">
                        Vos visiteurs verront le code immédiatement dès leur arrivée. Un seul code public à la fois — le plus récent prime.
                      </span>
                    </label>
                  </div>
                  <div className="md:col-span-3 flex justify-end">
                    <Button type="submit" className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl" data-testid="promo-create-submit">
                      <Plus size={16} className="mr-2" />
                      Créer le code
                    </Button>
                  </div>
                </form>
              </div>

              {/* Existing codes table */}
              <div className="bg-white rounded-2xl shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-warm-brown/10 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-warm-brown">Codes actifs et historique</h3>
                  <Button onClick={fetchPromoCodes} disabled={loadingPromos} variant="outline" className="rounded-xl" size="sm">
                    <RefreshCw className={`mr-2 ${loadingPromos ? 'animate-spin' : ''}`} size={14} />
                    Rafraîchir
                  </Button>
                </div>
                {loadingPromos ? (
                  <div className="p-12 text-center text-warm-brown-light">Chargement…</div>
                ) : promoCodes.length === 0 ? (
                  <div className="p-12 text-center text-warm-brown-light">
                    <Sparkles className="mx-auto mb-3 opacity-30" size={32} />
                    Aucun code promo pour l'instant.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-warm-brown/5 text-warm-brown text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-4 py-3 text-left">Code</th>
                          <th className="px-4 py-3 text-left">Description</th>
                          <th className="px-4 py-3 text-center">Réduction</th>
                          <th className="px-4 py-3 text-center">Expiration</th>
                          <th className="px-4 py-3 text-center">Utilisations</th>
                          <th className="px-4 py-3 text-center">Statut</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-warm-brown/10">
                        {promoCodes.map((p) => {
                          const valueLabel = p.discount_type === 'percentage'
                            ? `-${p.discount_value}%`
                            : `-${p.discount_value} €`;
                          const expLabel = p.valid_until
                            ? new Date(p.valid_until).toLocaleDateString('fr-FR')
                            : '—';
                          const usesLabel = p.max_uses
                            ? `${p.used_count} / ${p.max_uses}`
                            : `${p.used_count}`;
                          return (
                            <tr key={p.id} className="hover:bg-warm-brown/[.02]" data-testid={`promo-row-${p.code}`}>
                              <td className="px-4 py-3 font-mono font-bold text-warm-brown">{p.code}</td>
                              <td className="px-4 py-3 text-warm-brown-light">{p.description || '—'}</td>
                              <td className="px-4 py-3 text-center font-medium text-rose-600">{valueLabel}</td>
                              <td className="px-4 py-3 text-center text-xs">{expLabel}</td>
                              <td className="px-4 py-3 text-center">{usesLabel}</td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-warm-brown/10 text-warm-brown-light'
                                }`}>
                                  {p.is_active ? 'Actif' : 'Désactivé'}
                                </span>
                                {p.is_public && (
                                  <span className="block mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-100 text-rose-700">
                                    🎁 Bandeau public
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex gap-2 justify-end">
                                  <Button
                                    onClick={() => handleToggleBanner(p)}
                                    size="sm"
                                    variant="outline"
                                    className={`text-xs rounded-lg ${p.is_public ? 'bg-rose-50 border-rose-300 text-rose-700' : ''}`}
                                    data-testid={`promo-toggle-banner-${p.code}`}
                                    title="Afficher / cacher en bandeau public"
                                  >
                                    {p.is_public ? 'Retirer du bandeau' : 'En bandeau'}
                                  </Button>
                                  <Button
                                    onClick={() => handleTogglePromo(p)}
                                    size="sm"
                                    variant="outline"
                                    className="text-xs rounded-lg"
                                    data-testid={`promo-toggle-${p.code}`}
                                  >
                                    {p.is_active ? 'Désactiver' : 'Activer'}
                                  </Button>
                                  <Button
                                    onClick={() => handleDeletePromo(p)}
                                    size="sm"
                                    variant="outline"
                                    className="text-xs rounded-lg text-red-600 hover:bg-red-50 border-red-200"
                                    data-testid={`promo-delete-${p.code}`}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Admin;
